import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Tab,
  Tabs,
  Form,
  Row,
  Col,
  Button,
  Navbar,
  Container,
  Table,
  Spinner,
} from "react-bootstrap";
import InternalApi from "../../utils/axios.InternalConfig";
import FileUploadAPI from "../../utils/axios.fileUpload";
import { error, log } from "../../utils/logger";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { get } from "lodash";
import { isDev, isLocal } from "../../utils/env";

interface fileUpload {
  user: object;
  configs: object;
  fileType: string;
  filePath: string;
  cpp: any;
  title: string;
  fileUploadTypeName: string;
}
interface File {
  id: number;
  cppId_bi: number;
  filename_vch: string;
  filetype_vch: string;
  incident_command_vch: string;
  createdAt_utc: Date;
  status_vch: string;
}

interface FileUploadResponse {
  data: {
    fileName: string;
    mbId: string;
    status: number;
    message: string;
  };
}

const FileUpload = ({
  user,
  configs,
  fileType,
  filePath,
  cpp,
  title,
  fileUploadTypeName,
}: fileUpload) => {
  // All States
  const [incidentCommand, setIncidentCommand] = useState("");
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [canEditPreferences, setCanEditPreferences] = useState(false);
  const [cppName, setCPPName] = useState("");
  const [dataFile, setFileName] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef: any = useRef(null);
  const incidentCommandList = [
    { label: "Active", name: "active" },
    { label: "Active Overnight", name: "activeovernight" },
    { label: "Inactive", name: "inactive" },
  ];

  const Loader = () => (
    <div className="d-flex justify-content-center align-items-stretch" style={{ zIndex: 99 }}>
      <div className="my-auto">
        <Spinner animation="border" role="status" size="sm" />
      </div>
    </div>
  );

  useEffect(() => {
    const permissions: string[] = get(user, "details.permissions") || [];
    const fileUpload: any = permissions.find((p) => p.includes("fileUpload"));
    if (fileUpload) {
      setCanEditPreferences(fileUpload.includes("readWrite"));
    }
  }, [user]);

  const processFileList = (files: File[]) => {
    let queuedFiles: any = [];
    let allFiles: any = [];
    files.forEach((file: File) => {
      if (file?.status_vch === "queued") {
        queuedFiles.push(file);
      } else {
        allFiles.push(file);
      }
    });
    setQueuedFiles(queuedFiles);
    setAllFiles(allFiles);
  };

  const showSuccessMessage = (message: string) => {
    toastr.success("Success", message, { timeOut: 3000 });
  };

  const showErrorMessage = (message: string) => {
    toastr.error("Error", message, { timeOut: 3000 });
  };

  // Get list of files uploaded
  const getFiles = () => {
    InternalApi.post(`/files`, {
      fileType: fileUploadTypeName,
    })
      .then((res) => {
        const { data } = res;
        if (data.incidentCommand?.value_vch) {
          setIncidentCommand(data.incidentCommand?.value_vch);
        }
        processFileList(data.fileList);
      })
      .catch((err) => {
        error("Error creating file logs", err.response);
        showErrorMessage("Something went wrong fetching files");
      })
      .finally(() => {
        log("file log created successfully");
      });
  };
  useEffect(() => {
    getFiles();
  }, []);
  useEffect(() => {
    setCPPName(cpp?.current?.key?.toUpperCase() || "");
  }, [cpp]);

  const updateIncidentCommand = (e: any) => {
    e.stopPropagation();
    InternalApi.post(`/files/updateIncidentCommand`, {
      fileType: fileUploadTypeName,
      incidentCommand: incidentCommand,
    })
      .then((res) => {
        const { data } = res;
        if (data.incidentCommand?.value_vch) {
          setIncidentCommand(data.incidentCommand?.value_vch);
          processFileList(data.fileList);
        }
        showSuccessMessage("Incident command updated successfully");
      })
      .catch((err) => {
        error("File List API Error", err.response);
        showErrorMessage("Something went wrong");
      })
      .finally(() => {
        log("Incident command updated.");
      });
  };
  const createFileLog = (e: any) => {
    if (isLoading) {
      return;
    }
    if (!dataFile) {
      showErrorMessage("Please select a file to upload");
      resetFileInput();
      return;
    }
    e.stopPropagation();
    setIsLoading(true);
    uploadSFTPFile();
  };

  const writeFileLogToDB = (newFileName: string, mbId: string) => {
    InternalApi.post(`/files/fileLog`, {
      name: newFileName,
      type: fileUploadTypeName,
      incidentCommand: incidentCommand,
      status: "queued",
      description: mbId,
    })
      .then((res) => {
        getFiles();
        log("File logged in DB");
      })
      .catch((err: any) => {
        showErrorMessage("Something went wrong");
      })
      .finally(() => {
        log("File entry call processed");
      });
  };

  const resetFileInput = () => {
    inputRef.current.value = null;
  };

  const getExtension = (uploadedFileName: string) => {
    return uploadedFileName.split(".").pop();
  };

  const checkValidFileName = (uploadedFileName: string) => {
    const nameWithoutExtension = uploadedFileName && uploadedFileName.replaceAll(".csv", "");
    let validOpcoExtension: any = nameWithoutExtension && nameWithoutExtension.split("_");
    validOpcoExtension = validOpcoExtension[validOpcoExtension?.length - 1];
    if (validOpcoExtension !== cppName) {
      return false;
    }
    return true;
  };

  const verifyFileNFileExtension = (name: string) => {
    const extension = getExtension(name)?.toLowerCase();
    if (extension !== "csv") {
      resetFileInput();
      showErrorMessage("Please select a proper file extension");
      return false;
    }
    const validFileName: boolean = checkValidFileName(name);
    if (!validFileName) {
      resetFileInput();
      showErrorMessage("Please select a proper file name");
      return false;
    }
    return true;
  };

  const uploadFile = (e: any) => {
    if (!e.target.files?.length) {
      resetFileInput();
      setFileName(null);
      return;
    }
    const uploadedFile = e.target.files && e.target.files[0];
    const isValid: boolean = verifyFileNFileExtension(uploadedFile?.name);
    if (!isValid) return false;
    setFileName(uploadedFile);
  };

  const uploadSFTPFile = () => {
    const data = {
      type: fileUploadTypeName,
      isDev: isLocal,
    };
    const formData = new FormData();
    formData.append("file", dataFile);
    formData.append("data", JSON.stringify(data));
    FileUploadAPI.post(`files/fileUpload`, formData, {
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    })
      .then((res: FileUploadResponse) => {
        const { data } = res;
        writeFileLogToDB(data.fileName, data.mbId);
        showSuccessMessage("File uploaded successfully");
      })
      .catch((err: any) => {
        showErrorMessage("Something went wrong");
      })
      .finally(() => {
        log("File Uploaded process complete");
        resetFileInput();
        setIsLoading(false);
        setFileName(null);
      });
  };

  const getFileRows = (files: File[]) => {
    if (files?.length) {
      return files.map((file: File, index) => (
        <tr key={index}>
          <td>{index + 1}</td>
          <td>{file.filename_vch}</td>
          <td style={{ textTransform: "capitalize" }}>{file.status_vch}</td>
          <td>{moment.utc(file?.createdAt_utc, "YYYYMMDDHH").local().format("MMM Do YYYY hA")}</td>
        </tr>
      ));
    } else {
      return (
        <tr>
          <td colSpan={4} style={{ textAlign: "center" }}>
            No record found.
          </td>
        </tr>
      );
    }
  };
  return (
    <div className="container">
      <h4 className="mt-3 mb-3">{title}</h4>
      {(fileType === "lse" && (
        <div className="mb-3">
          <h6>Incident Command</h6>
          <Row>
            {incidentCommandList.map((incidentCommandElement, index) => {
              return (
                <Form.Check
                  key={index}
                  className="ml-3"
                  style={{ display: "inline-flex", alignItems: "center" }}
                  type={"radio"}
                  id={incidentCommandElement.name}
                  label={incidentCommandElement.label}
                  value={incidentCommandElement.name}
                  checked={incidentCommand === incidentCommandElement.name}
                  onChange={(e) => setIncidentCommand(incidentCommandElement.name)}
                />
              );
            })}
          </Row>
          <Button
            variant="primary"
            disabled={!canEditPreferences}
            className="mt-3"
            onClick={(e) => updateIncidentCommand(e)}
            style={{ minWidth: "78px" }}
          >
            <span>Update</span>
          </Button>
        </div>
      )) ||
        null}
      <Tabs defaultActiveKey="home" id="uncontrolled-tab-example" className="mb-3 mt-3">
        <Tab eventKey="home" title="Files in Queue">
          <Row>
            <Col style={{ color: "red" }}>
              *Important: File name must be in the format filename_{`${cppName}`}.csv. To view the
              valid data format, please{" "}
              <a href={`/files/${filePath}_${cppName}.csv`} target="_blank" rel="noreferrer">
                click here
              </a>{" "}
              to download sample data.
            </Col>
          </Row>
          {canEditPreferences && (
            <div>
              <Row>
                <Navbar
                  className="bg-body-tertiary mt-3"
                  style={{
                    backgroundColor: "#eee",
                    display: "flex",
                    width: "100%",
                    padding: "0px 0px 0px 15px",
                    fontWeight: "bold",
                  }}
                >
                  <Container>
                    <Navbar.Brand href="javascript:void(0)">Data File</Navbar.Brand>
                  </Container>
                </Navbar>
              </Row>
              <Row>
                <div className="px-3 py-3">
                  <input type="file" onChange={(e) => uploadFile(e)} ref={inputRef} />
                </div>
              </Row>
              <Row>
                <Navbar
                  className="bg-body-tertiary"
                  style={{
                    backgroundColor: "#eee",
                    display: "flex",
                    width: "100%",
                    padding: "0px 0px 0px 15px",
                  }}
                >
                  <Container>
                    <Navbar.Brand href="javascript:void(0)">
                      <Button
                        variant="primary"
                        onClick={(e) => createFileLog(e)}
                        style={{ minWidth: "78px" }}
                      >
                        {isLoading ? <Loader /> : <span>Upload</span>}
                      </Button>
                    </Navbar.Brand>
                  </Container>
                </Navbar>
              </Row>
            </div>
          )}
          <Row className="mt-2">
            {/* Queued Files */}
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>File Queue</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>{getFileRows(queuedFiles)}</tbody>
            </Table>
          </Row>
        </Tab>
        {/* Processed Files */}
        <Tab eventKey="profile" title="Processed Files">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Processed File</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>{getFileRows(allFiles)}</tbody>
          </Table>
        </Tab>
      </Tabs>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
  cpp: state.cpp,
  configs: state.config.list,
});

export default connect(mapStateToProps)(FileUpload);
